import { useEffect } from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import Link from 'next/link';
import cartAccount from '@/public/icons/account/cart.svg';
import cartAccountFill from '@/public/icons/account/cart-fill.svg';
import homeAccount from '@/public/icons/account/home.svg';
import homeAccountFill from '@/public/icons/account/home-fill.svg';
import logoutAccount from '@/public/icons/account/logout.svg';
import { useCustomerContext } from '@/lib/customer/CustomerContext';
import i18n from '@/settings/i18n';

const t = i18n.init('account');

/** Height of the nav fixed to the bottom of the viewport below lg. */
const NAV_OFFSET = '82px';

const STYLES = {
  BASE: 'flex flex-col-reverse justify-between items-center p-3 py-4 w-full text-sm text-center text-gray-700 hover:bg-gray-300  border-b border-gray-200 lg:flex-row lg:text-left xl:px-6 hover:font-bold xl:text-base',
  ACTIVE:
    'flex flex-col-reverse justify-between items-center p-3 py-4 w-full text-sm text-center text-gray-700 bg-gray-300  rounded-t border-b-2 border-gray-700 lg:flex-row lg:text-left lg:border-b lg:border-gray-200 xl:px-6 font-bold xl:text-base',
};

export default function AccountNav() {
  const router = useRouter();
  const { logout } = useCustomerContext();
  const isOrderRoute =
    router.pathname.endsWith('/orders') ||
    router.pathname.endsWith('/return') ||
    router.pathname.endsWith('/warrantyClaim') ||
    router.pathname.endsWith('/help');
  const isAddressRoute = router.pathname.endsWith('/addresses');

  useEffect(() => {
    // Fixed to the bottom of the viewport below lg: lifts the country and cookie banners above it
    const root = document.documentElement;

    root.style.setProperty('--cookie-banner-offset', NAV_OFFSET);

    return () => {
      root.style.removeProperty('--cookie-banner-offset');
    };
  }, []);

  return (
    <div className="fixed bottom-0 left-0 z-30 w-full bg-white shadow-lg lg:static lg:w-2/12 lg:rounded lg:border lg:border-gray-200 lg:shadow-none">
      <div className="flex w-full lg:block">
        <Link
          href={'/account/orders'}
          className={isOrderRoute ? STYLES.ACTIVE : STYLES.BASE}
        >
          <span>
            <span className="hidden lg:inline-block">{t`My`}</span> {t`Orders`}
          </span>
          <div className="mb-2 h-5 w-5 lg:mb-0 lg:h-4 lg:w-4 xl:h-5 xl:w-5">
            <Image
              height={20}
              width={20}
              alt="cartAccount"
              src={isOrderRoute ? cartAccountFill : cartAccount}
            />
          </div>
        </Link>
        <Link
          href={'/account/addresses'}
          className={isAddressRoute ? STYLES.ACTIVE : STYLES.BASE}
        >
          <span>
            <span className="hidden lg:inline-block">{t`My`}</span>{' '}
            {t`Addresses`}
          </span>
          <div className="mb-2 h-5 w-5 lg:mb-0 lg:h-4 lg:w-4 xl:h-5 xl:w-5">
            <Image
              height={18}
              width={18}
              alt="homeAccount"
              src={isAddressRoute ? homeAccountFill : homeAccount}
            />
          </div>
        </Link>
        <a
          className={`${STYLES.BASE} cursor-pointer`}
          onClick={async () => logout().then(() => router.push('/'))}
        >
          {t`Logout`}
          <div className="mb-2 h-5 w-5 lg:mb-0 lg:h-4 lg:w-4 xl:h-5 xl:w-5">
            <Image
              height={20}
              width={20}
              alt="logoutAccount"
              src={logoutAccount}
            />
          </div>
        </a>
      </div>
    </div>
  );
}
